'use client';

import type { FooterProps } from '../../../ui';
import type { IconName } from '../../icons';

import { cn } from '@polyseme/utils/cn/cn-utils';
import {
  type ComponentPropsWithoutRef,
  type ReactNode,
  isValidElement,
  useEffect,
  useState,
} from 'react';

import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  FooterButtons,
} from '../../../ui';
import { TooltipTrigger } from '../../../ui/tooltip';
import { Badge, type BadgeProps } from '../../badge';
import { Icon } from '../../icons';
import { Tooltip } from '../../tooltip';
import { Typography } from '../../typography';

export const sizes = ['sm', 'md', 'lg'] as const;

const sizeToTitleVariant = {
  sm: 'title-6',
  md: 'title-5',
  lg: 'title-4',
} as const;

const iconSizeToVariant = {
  sm: 'xs',
  md: 'sm',
  lg: 'md',
} as const;

export const collapsibleModes = ['none', 'collapsed', 'expanded'] as const;

export type SimpleCardProps = ComponentPropsWithoutRef<'div'> & {
  badge?: Pick<
    BadgeProps,
    'children' | 'className' | 'icon' | 'showIndicator' | 'size' | 'variant'
  >;
  className?: string;
  collapsibleMode?: (typeof collapsibleModes)[number];
  footer?: FooterProps;
  heading?: ReactNode;
  icon?: IconName;
  rightContent?: ReactNode;
  size?: (typeof sizes)[number];
  title?: string;
  titleClassName?: string;
  tooltip?: string;
};

export const SimpleCard = ({
  badge,
  children,
  collapsibleMode = 'none',
  footer,
  heading,
  icon,
  rightContent,
  size = 'lg',
  title,
  titleClassName,
  tooltip,
  ...props
}: SimpleCardProps) => {
  const hasHeader = !!heading || !!title || !!rightContent;

  const [isOpen, setIsOpen] = useState(collapsibleMode !== 'collapsed');

  useEffect(() => {
    if (collapsibleMode === 'none') {
      setIsOpen(true);
    }
  }, [collapsibleMode]);

  return (
    <Card {...props}>
      {!!hasHeader && (
        <CardHeader
          className={cn({
            'pb-4': collapsibleMode !== 'none' && !isOpen,
          })}
        >
          <div className="flex w-full items-center justify-between md:justify-center">
            {!!title && (
              <div
                className={cn('flex min-w-0 flex-1 gap-3 md:gap-4', {
                  'items-start': !!heading,
                  'items-center': !heading,
                })}
              >
                {!!icon && (
                  <div className="shrink-0 rounded-lg bg-accent p-2.5 text-primary">
                    <Icon
                      className="hidden md:block"
                      name={icon}
                      size={iconSizeToVariant[size]}
                    />
                    <Icon className="md:hidden" name={icon} size="xs" />
                  </div>
                )}
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 md:flex-nowrap">
                    <CardTitle
                      // Truncate only from `md` up: on mobile the card is narrow
                      // and a single-line ellipsis hides most of a long title
                      // (e.g. "Rachat total ou rachat partiel : q…") — let it
                      // wrap on small screens instead.
                      className={cn(
                        'font-semibold md:truncate',
                        titleClassName
                      )}
                      variant={sizeToTitleVariant[size]}
                    >
                      {title}
                    </CardTitle>

                    {!!tooltip && (
                      <Tooltip content={tooltip}>
                        <TooltipTrigger asChild>
                          <Icon name="info" size="sm" />
                        </TooltipTrigger>
                      </Tooltip>
                    )}

                    {!!badge && (
                      <Badge
                        {...badge}
                        className={cn(badge.className, 'shrink-0 self-center')}
                      />
                    )}
                  </div>

                  {!!heading &&
                    (isValidElement(heading) ? (
                      heading
                    ) : (
                      <Typography className="mt-2 text-sm font-medium leading-5 text-muted-foreground">
                        {heading}
                      </Typography>
                    ))}
                </div>
              </div>
            )}

            {!!(rightContent || collapsibleMode !== 'none') && (
              <div className="flex gap-2">
                {rightContent}
                {collapsibleMode !== 'none' && (
                  <Button
                    isIcon
                    aria-label={
                      isOpen ? 'Replier la section' : 'Déplier la section'
                    }
                    size="lg"
                    variant="ghost"
                    onClick={() => setIsOpen((prev) => !prev)}
                  >
                    <Icon name={isOpen ? 'chevron-up' : 'chevron-down'} />
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardHeader>
      )}

      {!!children && !!isOpen && (
        <CardContent
          className={cn('flex-1', {
            'pt-6': !hasHeader,
          })}
        >
          {children}
        </CardContent>
      )}

      {!!footer && !!isOpen && (
        <CardFooter>
          <FooterButtons {...footer} size={size} />
        </CardFooter>
      )}
    </Card>
  );
};
SimpleCard.displayName = 'SimpleCard';
